import { iterateKeys, IterUtils } from "../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    createAbsolute,
    isModelPath,
    ModelPath,
    SimplePathToken,
} from "../../../../Common/ModelPath/ModelPath";
import { iterateViewModels } from "../../Controls/viewModelUtils";
import { BindingPath, ViewModel } from "../../Engine/Core/CoreTypes";
import { PathUtils } from "../../Engine/Core/PathUtils/PathUtils";
import { NodeViewModel } from "../../Engine/Core/Store/FormViewModel";
import { DraftMeta, FullFormState, IViewModelContainer } from "../../Engine/Core/Store/FullFormState";
import { getIn } from "../ReduxStateHelpers";

export interface ControlModel {
    isTooltipEnabled: boolean;
    value?: string;
    error?: string;
    warning?: string;
    description?: string;
    id?: string;
    children?: string[];
    autoValue?: string;
    disabled?: boolean;
    autoFlag?: boolean;
    visible?: boolean;
    disabledSending?: boolean;
    ignoreAutoFlag?: boolean;
}

export class ViewModelSelect {
    private readonly redefinedViewModelIndex?: number;
    private readonly useControlContext: boolean;

    public constructor(useContext: boolean, redefinedViewModelIndex?: number) {
        this.useControlContext = useContext;
        this.redefinedViewModelIndex = redefinedViewModelIndex;
    }

    public withNextViewModel(): ViewModelSelect {
        return new ViewModelSelect(
            true,
            this.redefinedViewModelIndex === undefined ? 0 : this.redefinedViewModelIndex + 1
        );
    }

    public controlModel(state: IViewModelContainer, bindingPath: BindingPath | AbsoluteModelPath): ControlModel {
        const modelValue = this.pathViewModel(state, bindingPath);

        let autoFlag = modelValue.autoFlag;
        if (typeof autoFlag === "string") {
            autoFlag = autoFlag === "true";
        }
        const bindingPathWithoutStar = PathUtils.trimLastStarIfLastToken(bindingPath);
        const tooltipEnabled = !state.errorLogTooltip?.bindingPaths.some(bp => bp.includes(bindingPathWithoutStar));
        return {
            isTooltipEnabled: tooltipEnabled,
            value: modelValue.value,
            description: modelValue.description,
            id: modelValue.id,
            error: modelValue.error?.[0],
            warning: modelValue.warning?.[0],
            children: modelValue.children,
            autoValue: modelValue.autoValue,
            disabled: typeof modelValue.disabled === "string" ? modelValue.disabled === "true" : modelValue.disabled,
            visible: modelValue.visible,
            disabledSending: modelValue.disabledSending,
            autoFlag: autoFlag,
            ignoreAutoFlag: modelValue.ignoreAutoFlag,
        };
    }

    public pathViewModel(state: IViewModelContainer, bindingPath: BindingPath | AbsoluteModelPath): ViewModel {
        if (isModelPath(bindingPath)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(
                state,
                IterUtils.concat(bindingPath.normalize().getSimpleTokensStrict(), ["."]),
                {}
            );
        } else {
            const path = bindingPath.startsWith("/") ? bindingPath.split("/").slice(1) : bindingPath.split("/");
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(state, [...path, "."], {});
        }
    }

    public pathViewModelOrUndefined(
        state: IViewModelContainer,
        bindingPath: ModelPath | BindingPath
    ): ViewModel | undefined {
        if (isModelPath(bindingPath)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(state, IterUtils.concat(bindingPath.normalize().getSimpleTokensStrict(), ["."]));
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(state, [...bindingPath.split("/"), "."]);
        }
    }

    public node(state: IViewModelContainer, bindingPath: BindingPath | AbsoluteModelPath): NodeViewModel {
        const keys = this.splitBindingPathToIterableTokens(bindingPath);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.viewModelGetIn(state, keys, {});
    }

    private splitBindingPathToIterableTokens(bindingPath: BindingPath | AbsoluteModelPath): Iterable<SimplePathToken> {
        if (isModelPath(bindingPath)) {
            return bindingPath.normalize().getSimpleTokensStrict();
        }

        return bindingPath.split("/");
    }

    public customFieldValue(
        state: IViewModelContainer,
        bindingPath: BindingPath | AbsoluteModelPath,
        customValueFieldName: string,
        defaultValue: string = ""
    ): string {
        if (PathUtils.isPicklistFieldValueName(customValueFieldName)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(
                state,
                [
                    ...(isModelPath(bindingPath)
                        ? bindingPath.normalize().getSimpleTokensStrict()
                        : createAbsolute(bindingPath).normalize().getSimpleTokensStrict()),
                    ".",
                    "picklist",
                    PathUtils.getPicklistFieldName(customValueFieldName),
                ],
                defaultValue
            );
        }
        if (customValueFieldName === "$errorCount") {
            return this.getErrorCount(
                state,
                isModelPath(bindingPath) ? bindingPath : createAbsolute(bindingPath)
            ).toString();
        }
        if (customValueFieldName === "$warningCount") {
            return this.getWarningCount(
                state,
                isModelPath(bindingPath) ? bindingPath : createAbsolute(bindingPath)
            ).toString();
        }

        const fieldValue = this.viewModelGetIn(
            state,
            [
                ...(isModelPath(bindingPath)
                    ? bindingPath.normalize().getSimpleTokensStrict()
                    : createAbsolute(bindingPath).normalize().getSimpleTokensStrict()),
                ".",
                customValueFieldName,
            ],
            defaultValue
        );

        if (customValueFieldName === "errorsCount" || customValueFieldName === "warningsCount") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return fieldValue == undefined ? "0" : typeof fieldValue === "number" ? fieldValue.toString() : fieldValue;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return fieldValue;
    }

    public getErrorCount(state: IViewModelContainer, modelPath: AbsoluteModelPath): number {
        let result = 0;
        for (const viewModel of iterateViewModels(this.node(state, modelPath))) {
            if (viewModel.error != undefined && viewModel.error.length > 0) {
                result++;
            }
        }
        return result;
    }

    public getWarningCount(state: IViewModelContainer, modelPath: AbsoluteModelPath): number {
        let result = 0;
        for (const viewModel of iterateViewModels(this.node(state, modelPath))) {
            if (viewModel.warning != undefined && viewModel.warning.length > 0) {
                result++;
            }
        }
        return result;
    }

    public selectPicklist<Value>(
        state: IViewModelContainer,
        bindingPath: BindingPath | AbsoluteModelPath
    ): Value | undefined {
        return this.getCustomField(state, bindingPath, "picklist");
    }

    private getCustomField<Value>(
        state: IViewModelContainer,
        bindingPath: BindingPath | AbsoluteModelPath,
        field: string | string[]
    ): Value {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.viewModelGetIn(state, this.pathToTokens(bindingPath).concat(".", field));
    }

    private pathToTokens(path: BindingPath | AbsoluteModelPath) {
        return isModelPath(path)
            ? path.normalize().getSimpleTokensAsArrayStrict()
            : createAbsolute(path).normalize().getSimpleTokensAsArrayStrict();
    }

    public customFieldValueOrUndefined(
        state: IViewModelContainer,
        bindingPath: BindingPath | AbsoluteModelPath,
        customValueFieldName: string
    ): undefined | string {
        if (PathUtils.isPicklistFieldValueName(customValueFieldName)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(state, [
                ...(isModelPath(bindingPath)
                    ? bindingPath.normalize().getSimpleTokensStrict()
                    : createAbsolute(bindingPath).normalize().getSimpleTokensStrict()),
                ".",
                "picklist",
                PathUtils.getPicklistFieldName(customValueFieldName),
            ]);
        }
        if (customValueFieldName === "$errorCount") {
            return this.getErrorCount(
                state,
                isModelPath(bindingPath) ? bindingPath : createAbsolute(bindingPath)
            ).toString();
        }
        if (customValueFieldName === "$warningCount") {
            return this.getWarningCount(
                state,
                isModelPath(bindingPath) ? bindingPath : createAbsolute(bindingPath)
            ).toString();
        }

        const fieldValue = this.viewModelGetIn(state, [
            ...(isModelPath(bindingPath)
                ? bindingPath.normalize().getSimpleTokensStrict()
                : createAbsolute(bindingPath).normalize().getSimpleTokensStrict()),
            ".",
            customValueFieldName,
        ]);

        if (customValueFieldName === "errorsCount" || customValueFieldName === "warningsCount") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return fieldValue == undefined ? "0" : typeof fieldValue === "number" ? fieldValue.toString() : fieldValue;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return fieldValue;
    }

    public children(
        state: IViewModelContainer,
        bindingPath: BindingPath | AbsoluteModelPath,
        defaultValue: string[] = []
    ): readonly string[] {
        if (isModelPath(bindingPath)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(
                state,
                IterUtils.concat(bindingPath.normalize().getSimpleTokensStrict(), [".", "children"]),
                defaultValue
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.viewModelGetIn(state, [...bindingPath.split("/"), ".", "children"], defaultValue);
        }
    }

    public draftMeta(state: FullFormState): DraftMeta | undefined {
        const rootElement = this.getRootElement(state);
        if (rootElement == undefined) {
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.viewModelGetIn(state, [rootElement, "."]);
    }

    public getRootElement(state: FullFormState): string | undefined {
        return IterUtils.first(iterateKeys(state.viewModel));
    }

    private viewModelGetIn(
        state: Partial<IViewModelContainer> = {},
        keys: Iterable<undefined | number | string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        defaultValue?: any
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any {
        let viewModel = state.viewModel;

        if (state.redefinedViewModels) {
            if (this.useControlContext) {
                if (this.redefinedViewModelIndex !== undefined) {
                    viewModel = state.redefinedViewModels[this.redefinedViewModelIndex];
                }
            } else {
                if (state.redefinedViewModels.length > 0) {
                    viewModel = state.redefinedViewModels[state.redefinedViewModels.length - 1];
                }
            }
        }

        // На всякий случай
        if (!viewModel) {
            // eslint-disable-next-line no-console
            console.error("viewModelGetIn пытается обратиться к redefinedViewModels равной undefined");
            viewModel = state.viewModel;
        }

        const result = getIn(viewModel, keys, defaultValue);
        return result ?? defaultValue;
    }
}

export const currentViewModelSelect = new ViewModelSelect(false);
